/**
 * Copyright Mántum All Rights Reserved.
 */
import { useGroup, ACTION_VALUE } from '../../group';
import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from 'react';

import type { Validation, Detail } from '@mantum/kanela';

export type PropTypes = {
    disabled?: boolean;
    label?: string | React.ReactNode;
    name?: string;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onChange?: (name?: string, value?: unknown) => void;
    optional?: boolean;
    testid?: string;
    validation?: Validation;
    value?: boolean;
};

export default forwardRef<Detail<unknown>, PropTypes>(
    function Checkbox(props, ref) {
        // prettier-ignore
        const {
        name,
        optional = true,
        value = false
    } = props

        const changeRef = useRef(props.onChange);
        const inputRef = useRef<HTMLInputElement>(null);

        const groups = useGroup();

        const [error, setError] = useState(false);
        const [currentValue, setCurrentValue] = useState<boolean>(value);

        useEffect(() => {
            setCurrentValue(value);
        }, [value]);

        useEffect(() => {
            if (changeRef.current) {
                changeRef.current(name, value);
            }
        }, [name, value]);

        useImperativeHandle(
            ref,
            () => {
                return {
                    name,
                    validable: !optional,
                    clear() {
                        setError(false);
                        setCurrentValue(false);
                        if (changeRef.current) {
                            changeRef.current(name, false);
                        }
                    },
                    validate() {
                        setError(false);
                        const result = inputRef.current?.checked;
                        if (!result) {
                            setError(true);
                            return false;
                        }
                        return true;
                    },
                    value() {
                        return inputRef.current?.checked;
                    }
                };
            },
            [name, optional]
        );

        useEffect(() => {
            if (!name || !groups) {
                return;
            }

            if (!(name in groups)) {
                return;
            }

            const content = groups[name];
            if (content.action === ACTION_VALUE) {
                const value = content.value === true;
                setCurrentValue(value);

                if (changeRef.current) {
                    changeRef.current(name, value);
                }
            }
        }, [name, groups]);

        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const { name, checked } = event.target;

            setCurrentValue(checked);
            if (changeRef.current) {
                changeRef.current(name, checked);
            }
        };

        const style = error ? 'decoration-wavy decoration-error underline' : '';

        return (
            <div className='flex w-full items-center'>
                <label className={`flex items-center gap-2 ${style}`}>
                    <input
                        checked={currentValue}
                        className='h-4 w-4 accent-primary outline-primary'
                        data-testid={props.testid}
                        disabled={props.disabled}
                        name={props.name}
                        onBlur={props.onBlur}
                        onChange={handleChange}
                        ref={inputRef}
                        type='checkbox'
                    />
                    {props.label}
                </label>
            </div>
        );
    }
);
